import React, { useState, useEffect, useCallback } from "react";
import { useSelector } from "react-redux";
import { Link } from "react-router-dom";
import { getFriendSuggestions } from "../../functions/user";
import Card from "./Card";
import "./style.css";

const HALO_COLORS = [
  ["#fde4ef", "#fff3d6"],
  ["#fff3d6", "#e8f8e4"],
  ["#f3e8ff", "#e8f4ff"],
  ["#d9f5e4", "#e8f8d8"],
];

export default function Suggestions() {
  const { user } = useSelector((state) => ({ ...state }));
  const [suggestions, setSuggestions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [isRefreshing, setIsRefreshing] = useState(false);

  const shuffleArray = (array) => {
    const newArray = [...array];
    for (let i = newArray.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [newArray[i], newArray[j]] = [newArray[j], newArray[i]];
    }
    return newArray;
  };

  const getSuggestions = useCallback(async (shouldShuffle = false) => {
    try {
      setLoading(true);
      const response = await getFriendSuggestions(user.token);
      if (response.status === "ok") {
        const suggestionsData = response.data.suggestions;
        const finalSuggestions = shouldShuffle ? shuffleArray(suggestionsData) : suggestionsData;
        setSuggestions(finalSuggestions);
        setError("");
      } else {
        setError(response);
      }
    } catch (err) {
      console.error("Error fetching suggestions:", err);
      setError("Failed to load suggestions");
    } finally {
      setLoading(false);
    }
  }, [user.token]);

  useEffect(() => {
    getSuggestions();
  }, [getSuggestions]);

  const refreshSuggestions = async () => {
    setIsRefreshing(true);
    await getSuggestions(true);
    setTimeout(() => {
      setIsRefreshing(false);
    }, 500);
  };

  const header = (
    <div className="pymk_header">
      <div className="pymk_header_left">
        <div className="pymk_header_icon" aria-hidden="true">
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
            <path
              d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"
              stroke="currentColor"
              strokeWidth="1.8"
              strokeLinecap="round"
            />
            <circle cx="9" cy="7" r="4" stroke="currentColor" strokeWidth="1.8" />
            <path
              d="M19 8v6M22 11h-6"
              stroke="currentColor"
              strokeWidth="1.8"
              strokeLinecap="round"
            />
          </svg>
        </div>
        <div>
          <h3>People You May Know</h3>
          <p className="pymk_subtitle">Connect with new people and grow your circle</p>
        </div>
      </div>
      <div className="pymk_header_right">
        {suggestions.length > 0 && (
          <span className="suggestions_count">{suggestions.length} suggestions</span>
        )}
      </div>
    </div>
  );

  if (loading) {
    return (
      <div className="suggestions_container pymk_theme">
        {header}
        <div className="loading">Loading suggestions...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="suggestions_container pymk_theme">
        {header}
        <div className="error">{error}</div>
      </div>
    );
  }

  return (
    <div className="suggestions_container pymk_theme">
      {header}

      {suggestions.length === 0 ? (
        <div className="no_data">
          <div className="suggestion_icon">👥</div>
          <p>No suggestions available at the moment</p>
          <p className="sub_text">Check back later for new people to connect with!</p>
        </div>
      ) : (
        <div className="suggestions_grid">
          {suggestions.map((suggestion, index) => {
            const [haloA, haloB] = HALO_COLORS[index % HALO_COLORS.length];
            return (
              <div key={suggestion._id} className="suggestion_card">
                <Link to={`/profile/${suggestion.username || suggestion._id}`} className="suggestion_link">
                  <div className="suggestion_avatar">
                    <div
                      className="suggestion_avatar_halo"
                      style={{
                        background: `linear-gradient(135deg, ${haloA}, ${haloB})`,
                      }}
                    >
                      <img
                        src={suggestion.picture}
                        alt={suggestion.name}
                        onError={(e) => {
                          e.target.src = "https://res.cloudinary.com/dmhcnhtng/image/upload/v1643044376/avatars/default_pic_jeaybr.png";
                        }}
                      />
                    </div>
                  </div>
                  <div className="suggestion_info">
                    <div className="suggestion_name">{suggestion.name}</div>
                    <div className="suggestion_username">@{suggestion.username}</div>
                  </div>
                </Link>
                <div className="suggestion_actions">
                  <Card
                    userr={suggestion}
                    type="suggestion"
                    getData={() => getSuggestions(false)}
                  />
                </div>
              </div>
            );
          })}
        </div>
      )}

      {suggestions.length > 0 && (
        <div className="suggestions_footer">
          <button
            onClick={refreshSuggestions}
            className="refresh_suggestions"
            disabled={isRefreshing}
          >
            <span className={isRefreshing ? "spinning" : ""}>🔄</span>
            {isRefreshing ? "Shuffling..." : "Refresh Suggestions"}
          </button>
        </div>
      )}
    </div>
  );
}
